/**
 * SecureEmbed Dashboard - Audit Trail Health Card
 *
 * Displays cryptographic audit chain health across all 8 portals.
 * Shows chain verification status, event counts, and last verification time.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Fingerprint,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  Shield,
} from 'lucide-react';
import { metricsApi } from '../services/api';

const PORTAL_IDS = [
  { id: 'management', label: 'Management', color: '#1F2937' },
  { id: 'food', label: 'Food', color: '#DC2626' },
  { id: 'energy', label: 'Energy', color: '#7C3AED' },
  { id: 'education', label: 'Education', color: '#059669' },
  { id: 'medicine', label: 'Medicine', color: '#2563EB' },
  { id: 'tertiary', label: 'Tertiary', color: '#EA580C' },
  { id: 'innovateflow', label: 'InnovateFlow', color: '#0891B2' },
  { id: 'insurance', label: 'Insurance', color: '#CA8A04' },
  { id: 'theology', label: 'Theology', color: '#7E22CE' },
];

interface PortalChainStatus {
  portal_id: string;
  status: 'OK' | 'BROKEN' | 'EMPTY' | 'UNKNOWN';
  records_checked: number;
  last_verified?: string;
  broken_at?: string;
}

interface AuditHealthResponse {
  portals: PortalChainStatus[];
  total_events: number;
  last_global_verification?: string;
}

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'OK':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'BROKEN':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'EMPTY':
      return <Clock className="w-4 h-4 text-gray-400" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'OK': return 'Verified';
    case 'BROKEN': return 'Broken';
    case 'EMPTY': return 'No Events';
    default: return 'Unknown';
  }
};

const statusBgColor = (status: string) => {
  switch (status) {
    case 'OK': return 'bg-green-50 border-green-200';
    case 'BROKEN': return 'bg-red-50 border-red-200';
    case 'EMPTY': return 'bg-gray-50 border-gray-200';
    default: return 'bg-yellow-50 border-yellow-200';
  }
};

const formatTimestamp = (ts?: string) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return '—';
  }
};

const AuditTrailCard: React.FC = () => {
  const { data, isLoading, error, refetch } = useQuery<AuditHealthResponse>({
    queryKey: ['audit-health'],
    queryFn: async () => {
      try {
        const resp = await metricsApi.getAuditHealth();
        return resp;
      } catch {
        // Return default state if audit API not yet deployed
        return {
          portals: PORTAL_IDS.map(p => ({
            portal_id: p.id,
            status: 'UNKNOWN' as const,
            records_checked: 0,
          })),
          total_events: 0,
        };
      }
    },
    staleTime: 60000,
    refetchInterval: 60000,
  });

  const portals = data?.portals || [];
  const totalEvents = data?.total_events || 0;
  const okCount = portals.filter(p => p.status === 'OK').length;
  const brokenCount = portals.filter(p => p.status === 'BROKEN').length;

  const overallStatus = brokenCount > 0 ? 'critical' : okCount === PORTAL_IDS.length ? 'healthy' : 'warning';
  const overallColor = overallStatus === 'critical' ? 'border-red-300 bg-red-50/30' : overallStatus === 'healthy' ? 'border-green-300 bg-green-50/30' : 'border-yellow-300 bg-yellow-50/30';

  if (isLoading) {
    return (
      <div className="rounded-xl border-2 border-gray-200 p-5 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-gray-200 rounded-lg" />
          <div className="h-5 w-40 bg-gray-200 rounded" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border-2 p-5 ${overallColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Fingerprint className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Cryptographic Audit Trail</h3>
            <p className="text-xs text-gray-500">HMAC-SHA256 hash chain integrity</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Shield className="w-3.5 h-3.5" />
            <span>{totalEvents.toLocaleString()} events</span>
          </div>
          {data?.last_global_verification && (
            <span className="text-xs text-gray-400">
              Verified {formatTimestamp(data.last_global_verification)}
            </span>
          )}
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded-lg hover:bg-white/60 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh audit health"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Portal Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {PORTAL_IDS.map(portal => {
          const chain = portals.find(p => p.portal_id === portal.id) || {
            portal_id: portal.id,
            status: 'UNKNOWN' as const,
            records_checked: 0,
          };

          return (
            <div
              key={portal.id}
              className={`rounded-lg border px-3 py-2 ${statusBgColor(chain.status)}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold" style={{ color: portal.color }}>
                  {portal.label}
                </span>
                <StatusIcon status={chain.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500">
                  {chain.records_checked > 0
                    ? `${chain.records_checked.toLocaleString()} records`
                    : statusLabel(chain.status)}
                </span>
                {chain.last_verified && (
                  <span className="text-[10px] text-gray-400">
                    {formatTimestamp(chain.last_verified)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Audit health API unavailable — chain verification may not be deployed yet</span>
        </div>
      )}
    </div>
  );
};

export default AuditTrailCard;
